import React, { useState, useEffect } from "react";
import FileUpload from "./FileUpload.jsx";

function AdminFreightForm({ onSubmit, disabled, onUserChange, onDocumentUpload, uploadingDocs = {}, initialValues, existingDocuments = {}, resetKey, customerInfo, ensureDraft }) {
  const defaultFormData = {
    tradeType: "Import",
    containerType: "LCL",
    clearingAgentNum: "",
    fields: {},
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const [uploadMessages, setUploadMessages] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const updateFormData = (updater, options = { notify: true }) => {
    setFormData((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (options.notify && onUserChange) {
        onUserChange(next);
      }
      return next;
    });
  };

  useEffect(() => {
    if (initialValues) {
      updateFormData(initialValues, { notify: false });
    }
  }, [initialValues]);

  useEffect(() => {
    if (resetKey) {
      updateFormData(defaultFormData, { notify: false });
      setErrors({});
      setUploadMessages({});
      setUploadProgress({});
    }
  }, [resetKey]);

  const renderProgress = (field) => {
    const progress = uploadProgress[field];
    if (progress == null) return null;
    const isComplete = progress >= 100;
    return (
      <div style={{ marginTop: "6px" }}>
        <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: isComplete ? "#16a34a" : "#2563eb",
              transition: "width 0.2s ease",
            }}
          />
        </div>
        <div style={{ fontSize: "12px", color: isComplete ? "#16a34a" : "#2563eb", marginTop: "4px" }}>
          {isComplete ? "Upload complete" : `Uploading... ${progress}%`}
        </div>
      </div>
    );
  };

  const commitUserChanges = (next) => {
    if (onUserChange) {
      onUserChange(next ?? formData);
    }
  };

  const getLabel = (docType) => {
    const labels = {
      cbm: "CBM",
      pkgs: "Packages",
      pol: "Port Of Loading",
      pod: "Port Of Discharge",
      product: "Product/Items",
      incoTerms: "Incoterms",
      containerSize: "Size of Container",
      numberOfContainers: "No. of Containers",
      commonInvoices: "Commercial Invoices",
      packingLists: "Packing Lists",
      billOfLading: "Bill Of Lading",
      insurance: "Insurance",
      others: "Others",
    };
    return labels[docType] || docType;
  };

  const getFields = () => {
    const { tradeType, containerType } = formData;
    let result = [];
    if (tradeType === "Import" && containerType === "LCL") {
      result = ["cbm", "pkgs", "pol", "pod", "product", "incoTerms"];
    }
    if (tradeType === "Import" && containerType === "FCL") {
      result = [
        "containerSize",
        "numberOfContainers",
        "commonInvoices",
        "packingLists",
        "billOfLading",
        "insurance",
        "others",
      ];
    }
    if (tradeType === "Export" && containerType === "LCL") {
      result = [
        "cbm",
        "pkgs",
        "pol",
        "pod",
        "product",
        "incoTerms",
        "commonInvoices",
        "packingLists",
        "insurance",
        "others",
      ];
    }
    if (tradeType === "Export" && containerType === "FCL") {
      result = [
        "containerSize",
        "numberOfContainers",
        "pol",
        "pod",
        "product",
        "incoTerms",
        "commonInvoices",
        "packingLists",
        "insurance",
        "others",
      ];
    }
    return result;
  };

  const isTextField = (docType) =>
    [
      "cbm",
      "pkgs",
      "pol",
      "pod",
      "product",
      "incoTerms",
      "containerSize",
      "numberOfContainers",
    ].includes(docType);

  const handleTextChange = (field, value) => {
    updateFormData((prev) => ({ ...prev, fields: { ...prev.fields, [field]: value } }), { notify: false });
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleTextBlur = (field, value) => {
    const next = { ...formData, fields: { ...formData.fields, [field]: value } };
    updateFormData(next, { notify: false });
    commitUserChanges(next);
  };

  const handleFileChange = async (field, file, existingDocId) => {
    if (file && ensureDraft) {
      try {
        await ensureDraft();
      } catch (err) {
        console.error('Failed to create draft', err);
        return;
      }
    }
    updateFormData((prev) => ({ ...prev, fields: { ...prev.fields, [field]: file } }));
    setErrors((prev) => ({ ...prev, [field]: "" }));

    if (file && onDocumentUpload) {
      try {
        setUploadMessages((prev) => ({ ...prev, [field]: "" }));
        setUploadProgress((prev) => ({ ...prev, [field]: 0 }));
        await onDocumentUpload(file, field, existingDocId, (percent) => {
          setUploadProgress((prev) => ({ ...prev, [field]: percent }));
        });
        setUploadMessages((prev) => ({ ...prev, [field]: "Upload complete" }));
        setUploadProgress((prev) => ({ ...prev, [field]: 100 }));
      } catch (err) {
        setUploadMessages((prev) => ({ ...prev, [field]: `Upload failed: ${err.message}` }));
        setUploadProgress((prev) => {
          const updated = { ...prev };
          delete updated[field];
          return updated;
        });
        setErrors((prev) => ({ ...prev, [field]: err.message }));
        updateFormData((prev) => { const newFields = { ...prev.fields }; delete newFields[field]; return { ...prev, fields: newFields }; });
      }
    } else if (file) {
      setUploadMessages((prev) => ({ ...prev, [field]: "File selected" }));
    } else {
      setUploadMessages((prev) => { const updated = { ...prev }; delete updated[field]; return updated; });
    }
  };

  const handleClearingAgentNumChange = (e) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, "");
    const limitedValue = digitsOnly.slice(0, 11);
    updateFormData((prev) => ({ ...prev, clearingAgentNum: limitedValue }), { notify: false });
    if (errors.clearingAgentNum) setErrors((prev) => ({ ...prev, clearingAgentNum: "" }));
  };

  const handleClearingAgentNumBlur = (e) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, "");
    const limitedValue = digitsOnly.slice(0, 11);
    const next = { ...formData, clearingAgentNum: limitedValue };
    updateFormData(next, { notify: false });
    commitUserChanges(next);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const currentFields = getFields();
    const newErrors = {};

    currentFields.forEach((field) => {
      if (field === "insurance" || field === "others") return;
      if (!formData.fields[field] || formData.fields[field] === "") {
        newErrors[field] = `${getLabel(field)} is required`;
      }
    });

    if (formData.clearingAgentNum) {
      const digitsOnly = formData.clearingAgentNum.replace(/\D/g, "");
      if (digitsOnly.length !== 11) {
        newErrors.clearingAgentNum = "Clearing agent number must be exactly 11 digits";
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Map frontend field names to backend field names
    const fieldNameMap = {
      pkgs: 'packages',        // Frontend uses 'pkgs', backend expects 'packages'
      incoTerms: 'incoterms'   // Frontend uses 'incoTerms', backend expects 'incoterms'
    };

    // Start with top-level fields and customer info
    const payload = {
      requestType: "freight_forwarding", // always set
      tradeType: formData.tradeType,
      containerType: formData.containerType,
      clearingAgentNum: formData.clearingAgentNum,
      customer: customerInfo
    };

    // Flatten text fields from nested 'fields' object (skip file fields)
    const fileFieldNames = ["commonInvoices", "packingLists", "billOfLading", "insurance", "others"];
    Object.entries(formData.fields).forEach(([key, value]) => {
      if (!fileFieldNames.includes(key)) {
        const mappedKey = fieldNameMap[key] || key;
        if (key === 'cbm' || key === 'pkgs' || key === 'numberOfContainers') {
          payload[mappedKey] = value ? Number(value) : null;
        } else {
          payload[mappedKey] = value;
        }
      }
    });

    console.log('📦 Final freight submission payload:', JSON.stringify(payload, null, 2));

    // Submit flattened payload (no nested 'fields')
    onSubmit(payload);
  };

  const fields = getFields();
  const textFields = fields.filter(isTextField);
  const fileFields = fields.filter((f) => !isTextField(f));

  return (
    <form onSubmit={handleSubmit} className="space-y-6 frm-elem">
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center space-x-2">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <span>Freight Forwarding</span>
        </h3>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Trade Type</label>
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            <button type="button" onClick={() => updateFormData((prev) => ({ ...prev, tradeType: "Import", fields: {} }))} className={`px-6 py-3 rounded-full font-medium ${formData.tradeType === "Import" ? "bg-[#2c3e50] text-white" : "text-gray-700"}`}>Import</button>
            <button type="button" onClick={() => updateFormData((prev) => ({ ...prev, tradeType: "Export", fields: {} }))} className={`ml-1 px-6 py-3 rounded-full font-medium ${formData.tradeType === "Export" ? "bg-[#2c3e50] text-white" : "text-gray-700"}`}>Export</button>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Container Type</label>
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            <button type="button" onClick={() => updateFormData((prev) => ({ ...prev, containerType: "LCL", fields: {} }))} className={`px-6 py-3 rounded-full font-medium ${formData.containerType === "LCL" ? "bg-[#2c3e50] text-white" : "text-gray-700"}`}>LCL</button>
            <button type="button" onClick={() => updateFormData((prev) => ({ ...prev, containerType: "FCL", fields: {} }))} className={`ml-1 px-6 py-3 rounded-full font-medium ${formData.containerType === "FCL" ? "bg-[#2c3e50] text-white" : "text-gray-700"}`}>FCL</button>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Clearing Agent Number</label>
          <input type="text" value={formData.clearingAgentNum} onChange={handleClearingAgentNumChange} onBlur={handleClearingAgentNumBlur} placeholder="Enter 11-digit clearing agent number" maxLength={11} inputMode="numeric" pattern="[0-9]{11}" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500" style={{ padding: "8px 10px" }} />
          {errors.clearingAgentNum && <p className="text-red-500 text-xs mt-1 error-msg" style={{ color: 'red' }}>{errors.clearingAgentNum}</p>}
        </div>

        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {textFields.map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {getLabel(field)}
                  {field !== "insurance" && field !== "others" && (
                    <span className="text-red-500"> *</span>
                  )}
                </label>

                {field === "containerSize" ? (
                  <select value={formData.fields[field] || ""} onChange={(e) => handleTextChange(field, e.target.value)} onBlur={(e) => handleTextBlur(field, e.target.value)} className="w-full border rounded-lg p-2 text-sm border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500">
                    <option value="">Select Size</option>
                    <option value="20ft">20 ft</option>
                    <option value="40ft">40 ft</option>
                    <option value="flat_rack">Flat Rack</option>
                    <option value="reefer">Reefer</option>
                  </select>
                ) : (
                  <input type="text" value={formData.fields[field] || ""} onChange={(e) => handleTextChange(field, e.target.value)} onBlur={(e) => handleTextBlur(field, e.target.value)} placeholder={field === "incoTerms" ? "e.g: FOB, CFR, Ex-Works, etc" : `Enter ${getLabel(field)}`} className="w-full border rounded-lg p-2 text-sm border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500" />
                )}

                {errors[field] && <p className="text-red-500 text-xs mt-1 error-msg" style={{ color: 'red' }}>{errors[field]}</p>}
              </div>
            ))}

            {fileFields.map((field) => (
              <div key={field}>
                {existingDocuments[field] && !formData.fields[field] && (
                  <div className="text-xs text-slate-600 mb-1">Current: {existingDocuments[field].fileName || 'Document'}{' '}
                    {existingDocuments[field].signedUrl || existingDocuments[field].fileUrl ? (
                      <a href={existingDocuments[field].signedUrl || existingDocuments[field].fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Download</a>
                    ) : null}
                  </div>
                )}
                <FileUpload label={<>{getLabel(field)}{field !== "insurance" && field !== "others" && (<span className="text-red-500"> *</span>)}</>} onChange={(file) => handleFileChange(field, file, existingDocuments[field]?.id)} selectedFile={formData.fields[field]} />
                {renderProgress(field)}
                {uploadingDocs[field] && <p className="text-blue-600 text-sm mt-1">Uploading...</p>}
                {uploadMessages[field] && !uploadingDocs[field] && <p className="text-green-600 text-sm mt-1 success-msge" style={{ color: 'green' }}>{uploadMessages[field]}</p>}
                {errors[field] && <p className="text-red-500 text-xs mt-1 error-msg" style={{ color: 'red' }}>{errors[field]}</p>}
              </div>
            ))}
          </div>
        </div>

        <button type="submit" disabled={disabled} className="btn-modal-sub w-full bg-slate-600 hover:bg-slate-800 text-white py-3 px-6 rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed">{disabled ? "Submitting..." : "Submit Freight Forwarding Details"}</button>
      </div>
    </form>
  );
}

export default AdminFreightForm;